/**
 * useLoRa Hook
 *
 * Unified React hook for managing LoRa mesh connections.
 * Supports both Meshtastic and MeshCore backends transparently.
 *
 * ⚠️ EXPERIMENTAL: This integration is under active development.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import {
  isLoRaAvailable,
  getActiveBackend,
  getLoRaStatus,
  getLoRaDevice,
  setLoRaConfig,
  setLoRaEventHandlers,
  connectLoRa,
  disconnectLoRa,
  sendLoRaMessage,
  sendTextMessage,
  getAvailableBackends,
  getBackendDisplayName,
  isLoRaConnected,
} from '@/lib/lora'
import type {
  LoRaBackend,
  LoRaConnectionStatus,
  LoRaDevice,
  LoRaMessage,
  LoRaConfig,
  LoRaChannelMapping,
} from '@/lib/lora'

export interface UseLoRaOptions {
  /** Preferred backend */
  preferredBackend?: LoRaBackend
  /** Enable heartbeat */
  enableHeartbeat?: boolean
  /** Configuration overrides */
  config?: Partial<LoRaConfig>
  /** Callback when a message is received */
  onMessageReceived?: (message: LoRaMessage) => void
  /** Callback when device info changes */
  onDeviceInfo?: (device: LoRaDevice) => void
  /** Callback when an error occurs */
  onError?: (error: Error, backend: LoRaBackend) => void
}

export interface UseLoRaReturn {
  /** Is any LoRa backend available (Web Bluetooth) */
  isAvailable: boolean
  /** Available backends */
  availableBackends: LoRaBackend[]
  /** Active backend */
  activeBackend: LoRaBackend | null
  /** Current connection status */
  status: LoRaConnectionStatus
  /** Connected device info */
  device: LoRaDevice | null
  /** Is currently connecting */
  isConnecting: boolean
  /** Is connected and ready */
  isReady: boolean
  /** Is connected (any state) */
  isConnected: boolean
  /** Last error */
  error: Error | null
  /** Connect to a device */
  connect: (backend?: LoRaBackend) => Promise<void>
  /** Disconnect from device */
  disconnect: () => void
  /** Send a LoRa message */
  sendMessage: (message: LoRaMessage, channelMapping?: LoRaChannelMapping) => Promise<void>
  /** Send a text message (convenience) */
  sendText: (text: string, roomId: string) => Promise<void>
  /** Get backend display name */
  getBackendName: (backend: LoRaBackend) => string
}

export function useLoRa(options: UseLoRaOptions = {}): UseLoRaReturn {
  const {
    preferredBackend,
    // enableHeartbeat is reserved for future use
    config,
    onMessageReceived,
    onDeviceInfo,
    onError,
  } = options

  // State
  const [status, setStatus] = useState<LoRaConnectionStatus>('disconnected')
  const [device, setDevice] = useState<LoRaDevice | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const [isConnecting, setIsConnecting] = useState(false)

  // Refs for callbacks
  const onMessageReceivedRef = useRef(onMessageReceived)
  const onDeviceInfoRef = useRef(onDeviceInfo)
  const onErrorRef = useRef(onError)

  // Update refs when callbacks change
  useEffect(() => {
    onMessageReceivedRef.current = onMessageReceived
    onDeviceInfoRef.current = onDeviceInfo
    onErrorRef.current = onError
  }, [onMessageReceived, onDeviceInfo, onError])

  // Check availability
  const isAvailable = isLoRaAvailable()
  const availableBackends = getAvailableBackends()
  const activeBackend = getActiveBackend()

  // Derived state
  const isReady = status === 'ready'
  const isConnected = isLoRaConnected()

  // Apply configuration
  useEffect(() => {
    if (config || preferredBackend !== undefined) {
      setLoRaConfig({
        ...config,
        preferredBackend,
        debug: config?.debug ?? false,
      })
    }
  }, [config, preferredBackend])

  // Set up event handlers
  useEffect(() => {
    setLoRaEventHandlers({
      onStatusChange: (newStatus, _backend) => {
        setStatus(newStatus)
      },
      onDeviceInfo: (deviceInfo) => {
        setDevice(deviceInfo)
        onDeviceInfoRef.current?.(deviceInfo)
      },
      onMessageReceived: (message) => {
        onMessageReceivedRef.current?.(message)
      },
      onError: (err, backend) => {
        setError(err)
        onErrorRef.current?.(err, backend)
      },
      onDisconnect: (_backend) => {
        setDevice(null)
      },
    })

    return () => {
      setLoRaEventHandlers({})
    }
  }, [])

  // Sync state periodically
  useEffect(() => {
    const syncState = () => {
      setStatus(getLoRaStatus())
      const currentDevice = getLoRaDevice()
      if (currentDevice) {
        setDevice(currentDevice)
      }
    }

    // Initial sync
    syncState()

    // Periodic sync
    const interval = setInterval(syncState, 2000)
    return () => clearInterval(interval)
  }, [])

  // Connect
  const connect = useCallback(
    async (backend?: LoRaBackend) => {
      if (!isAvailable) {
        const err = new Error('Web Bluetooth is not available')
        setError(err)
        onErrorRef.current?.(err, backend || 'meshtastic')
        return
      }

      setIsConnecting(true)
      setError(null)

      try {
        const connectedDevice = await connectLoRa(backend || preferredBackend)
        setDevice(connectedDevice)
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err))
        setError(error)
        onErrorRef.current?.(error, backend || preferredBackend || 'meshtastic')
      } finally {
        setIsConnecting(false)
      }
    },
    [isAvailable, preferredBackend]
  )

  // Disconnect
  const disconnect = useCallback(() => {
    disconnectLoRa()
    setDevice(null)
    setError(null)
  }, [])

  // Send message
  const sendMessage = useCallback(
    async (message: LoRaMessage, channelMapping?: LoRaChannelMapping) => {
      if (!isConnected) {
        throw new Error('Not connected to LoRa device')
      }

      await sendLoRaMessage(message, channelMapping)
    },
    [isConnected]
  )

  // Send text (convenience)
  const sendText = useCallback(
    async (text: string, roomId: string) => {
      if (!isConnected) {
        throw new Error('Not connected to LoRa device')
      }

      await sendTextMessage(text, roomId)
    },
    [isConnected]
  )

  // Get backend name
  const getBackendName = useCallback((backend: LoRaBackend) => {
    return getBackendDisplayName(backend)
  }, [])

  return {
    isAvailable,
    availableBackends,
    activeBackend,
    status,
    device,
    isConnecting,
    isReady,
    isConnected,
    error,
    connect,
    disconnect,
    sendMessage,
    sendText,
    getBackendName,
  }
}
